import React, { useRef, useState, useEffect } from "react";
import glowCircleBg from "../resources/button_blue_gradient_circle_glow.png";

const ControlLightColourWheel = ({ width = 300, onColorChange, onBrightnessChange, brightness, colour }) => {
  const [selectedColour, setSelectedColour] = useState("#FFFFFF");
  const [selector, setSelector] = useState({ x: 0, y: 0 });
  const [isWhiteSelected, setIsWhiteSelected] = useState(false);
  const [selectedBrightness, setSelectedBrightness] = useState(null);

  const wheelRef = useRef(null);

  const viewWidth = width;
  const viewHeight = 300;
  const centerY = viewHeight / 2;

  const groupWidth = viewWidth * 0.9;
  const wheelPortion = groupWidth * 0.5;
  const gapBetweenElements = groupWidth * 0.8;
  const whitePortion = groupWidth * 0.35;

  const wheelRadius = wheelPortion * 0.55;
  const whiteRadius = whitePortion * 0.4;
  const innerHoleRadius = wheelRadius * 0.6;

  const totalWidth = wheelRadius * 2 + gapBetweenElements + whiteRadius * 2;
  const groupCenterX = viewWidth / 2;
  const leftStartX = groupCenterX - totalWidth / 2;

  const leftCenterX = leftStartX + wheelRadius;
  const rightCenterX = leftStartX + wheelRadius * 2 + gapBetweenElements + whiteRadius;

  const colourHexMap = {
    white: "#FFFFFF",
    red: "#FF0000",
    green: "#00FF00",
    blue: "#0000FF",
    orange: "#FFA500",
    teal: "#008080",
    purple: "#800080",
    pink: "#FFC0CB",
    lime: "#C0FF00",
  };

  const colourAngles = {
    red: 0,
    orange: 30,
    yellow: 60,
    lime: 90,
    green: 120,
    teal: 180,
    blue: 240,
    purple: 280,
    pink: 320,
    white: null,
  };

  function hsvToHsl(h, s, v) {
    const l = (2 - s) * v / 2;
    const sHSL = l === 0 || l === 1 ? 0 : (s * v) / (l <= 0.5 ? l * 2 : 2 - l * 2);
    return [h, sHSL, l];
  }

  function hsvToHslCss(h, s, v) {
    const [hue, sat, light] = hsvToHsl(h, s, v);
    return `hsl(${hue}, ${sat * 100}%, ${light * 100}%)`;
  }

  const handleColorChange = (angle) => {
    const hue = ((angle * 180) / Math.PI + 360) % 360;
    const color = `hsl(${hue}, 100%, 50%)`;
    setSelectedColour(color);
    onColorChange?.(color);
  };

  const handlePointer = (e) => {
    const rect = wheelRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const dxWhite = x - (rightCenterX - leftStartX);
    const dyWhite = y - centerY;
    const distWhite = Math.sqrt(dxWhite * dxWhite + dyWhite * dyWhite);

    const dxWheel = x - wheelRadius;
    const dyWheel = y - wheelRadius;
    const distWheel = Math.sqrt(dxWheel * dxWheel + dyWheel * dyWheel);

    const insideWhite = distWhite <= whiteRadius;
    const insideWheel = distWheel >= innerHoleRadius && distWheel <= wheelRadius;

    if (insideWhite) {
      setIsWhiteSelected(true);
      setSelectedColour("#FFFFFF");
      onColorChange?.("#FFFFFF");
    } else if (insideWheel) {
      const angle = Math.atan2(dyWheel, dxWheel);
      const ringInnerRadius = innerHoleRadius * 1.25;
      const ringOuterRadius = wheelRadius;
      const selectorRadius = (ringInnerRadius + ringOuterRadius) / 2;

      const ringX = wheelRadius + Math.cos(angle) * selectorRadius;
      const ringY = wheelRadius + Math.sin(angle) * selectorRadius;
      setSelector({ x: ringX, y: ringY });

      setIsWhiteSelected(false);
      handleColorChange(angle);
    }
  };

  useEffect(() => {
    if (brightness != null) {
      setSelectedBrightness(Number(brightness));
    }
  }, [brightness]);

  useEffect(() => {
    if (!colour || colour === "white") {
      setIsWhiteSelected(true);
      return;
    }

    const angle = colourAngles[colour.toLowerCase()];
    if (angle == null) return;

    const radians = (angle - 90) * (Math.PI / 180);

    const radius = wheelRadius - 10;
    const x = wheelRadius + radius * Math.cos(radians);
    const y = wheelRadius + radius * Math.sin(radians);

    setSelector({ x, y });
    setIsWhiteSelected(false);
    setSelectedColour(colour);
  }, [colour]);


  useEffect(() => {
    const angle = 0;
    const ringInnerRadius = innerHoleRadius * 1.25;
    const ringOuterRadius = wheelRadius;
    const selectorRadius = (ringInnerRadius + ringOuterRadius) / 2;

    setSelector({
      x: wheelRadius + Math.cos(angle) * selectorRadius,
      y: wheelRadius + Math.sin(angle) * selectorRadius,
    });
  }, [wheelRadius, innerHoleRadius]);

  const gradientStops = Array.from({ length: 360 }, (_, i) => {
    const color = hsvToHslCss(i, 1, 1);
    return `${color} ${i}deg ${(i + 1)}deg`;
  }).join(", ");

  return (
    <div
      style={{
        width: viewWidth,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      {/* --- Colour Wheel + White Selector --- */}
      <div
        style={{
          width: totalWidth,
          height: viewHeight,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          left: -80
        }}
      >
        <div
          ref={wheelRef}
          onPointerDown={handlePointer}
          onPointerMove={(e) => e.buttons === 1 && handlePointer(e)}
          style={{
            width: wheelRadius * 2,
            height: wheelRadius * 2,
            borderRadius: "50%",
            background: `conic-gradient(${gradientStops})`,
            position: "relative",
            touchAction: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              width: innerHoleRadius * 2.5,
              height: innerHoleRadius * 2.5,
              borderRadius: "50%",
              backgroundColor: "#000000",
            }}
          />

          {!isWhiteSelected && (
            <div
              style={{
                position: "absolute",
                left: selector.x - 10,
                top: selector.y - 10,
                width: 20,
                height: 20,
                borderRadius: "50%",
                border: "4px solid #FFFFFF",
                pointerEvents: "none",
              }}
            />
          )}
        </div>

        <div
          onPointerDown={() => {
            setIsWhiteSelected(true);
            setSelectedColour("#FFFFFF");
            onColorChange?.("#FFFFFF");
          }}
          style={{
            position: "absolute",
            left: wheelRadius * 2 + gapBetweenElements,
            top: viewHeight / 2 - whiteRadius,
            width: whiteRadius * 2,
            height: whiteRadius * 2,
            borderRadius: "50%",
            backgroundColor: "#FFFFFF",
            border: isWhiteSelected ? "3px solid #999" : "none",
            cursor: "pointer",
          }}
        />
      </div>

      {/* --- Brightness Section --- */}
      <div className="brightness-buttons">
        {[1, 2, 3, 4, 5].map((n) => {
          const isSelected = selectedBrightness === n;
          return (
            <div 
              key={n}
              onClick={() => {
                setSelectedBrightness(n);
                onBrightnessChange?.(n);
              }}
              style={{
                position: "relative",
                width: "90px",
                height: "90px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              <img
                src={glowCircleBg}
                alt="glow"
                style={{
                  position: "absolute",
                  width: "90px",
                  height: "90px",
                  objectFit: "contain",
                  opacity: isSelected ? 1 : 0,
                  transition: "opacity 0.3s",
                }}
              />
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "50%",
                  backgroundColor: "transparent",
                  border: isSelected ? "2px solid transparent" : "2px solid #2f3338",
                  zIndex: 1,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  transition: "all 0.2s ease",
                }}
              >
                <span style={{ color: "white", fontSize: "20px" }}>{n}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ControlLightColourWheel;
